import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import _ from 'lodash'
import useSWR from 'swr'
import PT from 'prop-types'
import { useSelector } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import MultiSelect from '../MultiSelect'
import api from '~/services/api'
import { Hide } from '~/components/commons'
import { brazilianStates } from '~/constants/location'
import { CommaArrayParam } from '~/hooks/useQueryParams'
import { CLIENT_ADMIN } from '~/constants/roles'
import { getLocalizedAddressVersion } from '~/utility/getLocalizedAddressVersion'

export const defaultQueryLocationsFilterParams = {
  projects: CommaArrayParam,
  area: CommaArrayParam,
  city: CommaArrayParam,
  state: CommaArrayParam,
  labels: CommaArrayParam,
  location: CommaArrayParam,
}

const selectsInitialValue = {
  projects: [],
  area: [],
  city: [],
  state: [],
  labels: [],
  location: [],
}

/**
 * Needed to pass your query parameters to the filter context and also the set to change it
 */
export const LocationsFilterContext = React.createContext({
  queryParams: defaultQueryLocationsFilterParams,
  setQueryParams: () => {},
})

function sortByLabel(a, b) {
  var textA = a.label.toUpperCase()
  var textB = b.label.toUpperCase()
  return textA < textB ? -1 : textA > textB ? 1 : 0
}

function findLocationsFromOption(obj, result = []) {
  if (_.isArray(obj)) {
    return result.concat(obj)
  }

  return Object.values(obj).reduce((acc, value) => {
    if (_.isArray(value)) {
      return acc.concat(value)
    }

    return acc.concat(findLocationsFromOption(value))
  }, [])
}

const getFilteredOptions = (options = [], selectedLocationNames = []) => {
  const filteredOptions = options.filter(
    option => _.intersection(option.value, selectedLocationNames).length
  )

  return _.sortBy(filteredOptions, 'label')
}

const hasSelectedFilters = filters => {
  const notEmptyFilters = _.pickBy(
    filters,
    filter => _.isArray(filter) && !_.isEmpty(filter)
  )

  return !_.isEmpty(notEmptyFilters)
}

const LocationNamesFilterWithParams = ({
  onChange,
  onUpdate,
  onInit,
  disabled,
  locationBased = false,
  noLabel = false,
}) => {
  const intl = useIntl()
  const projectId = useSelector(state => state.user?.selectedProject?.id)
  const canImportProjects = useSelector(
    state => state.user?.profile?.account?.importProject
  )
  const userProfile = useSelector(state => state.user?.profile)
  const isClientAdmin = userProfile?.role === CLIENT_ADMIN
  const [initialized, setInitialized] = useState(false)
  const [selects, setSelects] = useState(selectsInitialValue)
  const [showAllOptions, setShowAllOptions] = useState(true)
  const [touchedFilter, setTouchedFilter] = useState('')
  const [selectedLocationNames, setSelectedLocationNames] = useState([])
  const [currentProjectId, setCurrentProjectId] = useState()
  const [regionCodes, setRegionCodes] = useState([])

  const { queryParams, setQueryParams, scrollToTop } = useContext(
    LocationsFilterContext
  )

  const onChangeRef = useRef()
  onChangeRef.current = onChange

  const fetchMeta = async url => {
    const response = await api.get(url)
    return response.data
  }

  const { data: meta } = useSWR(
    locationBased ? '/locations/meta' : `/projects/${projectId}/locations/meta`,
    fetchMeta,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      revalidateOnMount: true,
    }
  )

  const filters = useMemo(() => {
    if (!queryParams) return {}

    return _.pick(queryParams, ['area', 'city', 'state', 'labels', 'location'])
  }, [queryParams])

  const { options } = useMemo(() => {
    if (!meta) return []

    const options = {
      projects: [],
      location: [],
      state: [],
      city: [],
      area: [],
      labels: [],
    }

    const { projects = {}, addresses, allLocations, labels, total } = meta

    const stateArray = []

    const cityArray = []

    const areaArray = []

    Object.entries(addresses).forEach(([administrativeArea, value]) => {
      const brazilianStateIndex = brazilianStates.findIndex(
        state =>
          state.state === administrativeArea || state.uf === administrativeArea
      )
      stateArray.push({
        label:
          brazilianStateIndex >= 0
            ? `${brazilianStates[brazilianStateIndex].state} - ${brazilianStates[brazilianStateIndex].uf}`
            : administrativeArea === ''
            ? intl.formatMessage({ id: 'No State' })
            : administrativeArea,
        value: findLocationsFromOption(value),
      })

      Object.entries(value).forEach(([locality, value]) => {
        cityArray.push({
          label:
            locality === 'undefined'
              ? intl.formatMessage({ id: 'No City' }) +
                (administrativeArea ? ` (${administrativeArea})` : '')
              : locality,
          value: findLocationsFromOption(value),
        })

        Object.entries(value).forEach(([sublocality, value]) => {
          areaArray.push({
            label:
              sublocality === 'undefined'
                ? intl.formatMessage({ id: 'No Address' }) +
                  (locality !== 'undefined' ? ` (${locality})` : '')
                : sublocality,
            value: findLocationsFromOption(value),
          })
        })
      })
    })

    stateArray.forEach(item => {
      var existing = options.state.filter(v => {
        return v.label === item.label
      })
      if (existing.length) {
        var existingIndex = options.state.indexOf(existing[0])
        options.state[existingIndex].value = options.state[
          existingIndex
        ].value.concat(item.value)
      } else {
        options.state.push(item)
      }
    })

    cityArray.forEach(item => {
      var existing = options.city.filter(v => {
        return v.label === item.label
      })
      if (existing.length) {
        var existingIndex = options.city.indexOf(existing[0])
        options.city[existingIndex].value = options.city[
          existingIndex
        ].value.concat(item.value)
      } else {
        options.city.push(item)
      }
    })

    areaArray.forEach(item => {
      var existing = options.area.filter(v => {
        return v.label === 'No Address' && item.label === 'No Address'
      })
      if (existing.length) {
        var existingIndex = options.area.indexOf(existing[0])
        options.area[existingIndex].value = options.area[
          existingIndex
        ].value.concat(item.value)
      } else {
        options.area.push(item)
      }
    })

    options.state.sort(sortByLabel)
    options.city.sort(sortByLabel)

    options.location = allLocations?.map(location => {
      const title = location?.title || ''
      const address = location?.address || ''
      const storeCode = location?.storeCode || ''
      return {
        label: {
          ...location,
          text: `${title} ${address} ${storeCode}`,
        },
        value: [location.name],
      }
    })

    options.labels = Object.entries(labels).map(([key, value]) => ({
      label: key,
      value: value,
    }))

    options.projects = Object.entries(projects).map(([key, value]) => ({
      label: key,
      value: value,
    }))

    const regions = meta?.allLocations?.map(item => item?.regionCode)
    const uniqueRegions = Array.from(new Set(regions))
    setRegionCodes(uniqueRegions)

    return {
      options,
      total,
      projects: Object.values(projects).reduce(
        (acc, cur) => (acc = acc.concat(cur)),
        []
      ),
      allNames: allLocations.map(location => location.name),
    }
  }, [meta, intl])

  const locationsOptions = {
    projects: useMemo(() => {
      if (!meta) return []
      if (showAllOptions || touchedFilter === 'projects')
        return options?.projects

      const hasFilters = hasSelectedFilters(selects)
      if (!hasFilters) return []

      return getFilteredOptions(options?.projects, selectedLocationNames)
    }, [
      selects,
      selectedLocationNames,
      meta,
      touchedFilter,
      options,
      showAllOptions,
    ]),

    state: useMemo(() => {
      if (!meta) return []
      if (showAllOptions || touchedFilter === 'state') return options?.state

      const hasFilters = hasSelectedFilters(selects)
      if (!hasFilters) return []

      return getFilteredOptions(options?.state, selectedLocationNames)
    }, [
      selects,
      selectedLocationNames,
      meta,
      touchedFilter,
      options,
      showAllOptions,
    ]),

    city: useMemo(() => {
      if (!meta) return []
      if (showAllOptions || touchedFilter === 'city') return options?.city

      const hasFilters = hasSelectedFilters(selects)
      if (!hasFilters) return []

      return getFilteredOptions(options?.city, selectedLocationNames)
    }, [
      selects,
      selectedLocationNames,
      meta,
      touchedFilter,
      options,
      showAllOptions,
    ]),

    area: useMemo(() => {
      if (!meta) return []
      if (showAllOptions || touchedFilter === 'area') return options?.area

      const hasFilters = hasSelectedFilters(selects)
      if (!hasFilters) return []

      return getFilteredOptions(options?.area, selectedLocationNames)
    }, [
      selects,
      selectedLocationNames,
      meta,
      touchedFilter,
      options,
      showAllOptions,
    ]),

    location: useMemo(() => {
      if (!meta) return []

      if (showAllOptions || touchedFilter === 'location')
        return options?.location

      const hasFilters = hasSelectedFilters(selects)
      if (!hasFilters) return []

      return getFilteredOptions(options?.location, selectedLocationNames)
    }, [
      selects,
      selectedLocationNames,
      meta,
      touchedFilter,
      options,
      showAllOptions,
    ]),

    labels: useMemo(() => {
      if (!meta) return []
      if (showAllOptions || touchedFilter === 'labels') return options?.labels

      const hasFilters = hasSelectedFilters(selects)
      if (!hasFilters) return []

      return getFilteredOptions(options?.labels, selectedLocationNames)
    }, [
      selects,
      selectedLocationNames,
      meta,
      touchedFilter,
      options,
      showAllOptions,
    ]),
  }

  const updateQueryParamsBySelects = useCallback(
    selectedValues => {
      const locationsFilters = {}
      for (const [key, values] of Object.entries(selectedValues)) {
        locationsFilters[key] = values?.map(v =>
          key === 'location' ? v.label.name : v.label
        )
      }

      setQueryParams(prevQuery => ({ ...prevQuery, ...locationsFilters }))
    },
    [setQueryParams]
  )

  const updateSelectedLocationNames = useCallback(selectedValues => {
    const locationNames = []
    for (const options of Object.values(selectedValues)) {
      const names = _.flatMap(options, option => option.value)
      locationNames.push(...names)
    }
    setSelectedLocationNames(_.uniq(locationNames))
  }, [])

  const updateTouchedField = useCallback(
    selectedValues => {
      for (const [key, values] of Object.entries(selectedValues)) {
        if (!touchedFilter && values.length) {
          setTouchedFilter(key)
          setShowAllOptions(false)
        }
      }
    },
    [setTouchedFilter, touchedFilter, setShowAllOptions]
  )

  const handleFilterValueChange = useCallback(
    _.debounce((filter, options) => {
      const willClear = () => {
        return !hasSelectedFilters(_.omit(selects, filter))
      }

      const willClearAllFilters = willClear()
      const hasSelected = hasSelectedFilters(selects)

      if (filter === touchedFilter && !options.length) {
        setShowAllOptions(true)
      }

      if (!hasSelected) {
        setTouchedFilter(filter)
        setShowAllOptions(false)
      }

      if (!options?.length && willClearAllFilters) {
        setTouchedFilter('')
        setShowAllOptions(true)
      }

      const nextSelects = {
        ...selects,
        [filter]: options,
      }

      setSelects(nextSelects)

      updateQueryParamsBySelects(nextSelects)
      updateSelectedLocationNames(nextSelects)

      if (_.isFunction(onUpdate)) {
        onUpdate(nextSelects)
      }
    }, 100),
    [selects, filters]
  )

  useEffect(() => {
    if (!queryParams || !options || initialized) return

    const { projects, area, city, state, labels, location } = queryParams

    const findByField = (labels, name, field) => {
      if (!_.isArray(labels) && !_.isUndefined(labels)) {
        labels = [labels]
      }

      return (labels || [])?.map(optionLabel =>
        options[name].find(option => {
          return _.get(option, field) === optionLabel
        })
      )
    }

    const selectsParsedOptions = {
      projects: findByField(projects, 'projects', 'label'),
      area: findByField(area, 'area', 'label'),
      city: findByField(city, 'city', 'label'),
      state: findByField(state, 'state', 'label'),
      labels: findByField(labels, 'labels', 'label'),
      location: findByField(location, 'location', 'label.name'),
    }

    updateTouchedField(selectsParsedOptions)
    updateSelectedLocationNames(selectsParsedOptions)
    setSelects(selectsParsedOptions)
    setInitialized(true)
    if (_.isFunction(onInit)) {
      onInit()
    }
  }, [
    queryParams,
    options,
    initialized,
    touchedFilter,
    onInit,
    updateTouchedField,
    updateSelectedLocationNames,
    updateQueryParamsBySelects,
  ])

  const scrollToTopScreen = useCallback(() => {
    if (_.isFunction(scrollToTop)) scrollToTop()
  }, [scrollToTop])

  useEffect(() => {
    setCurrentProjectId(projectId)
  }, [setCurrentProjectId, projectId])

  useEffect(() => {
    if (currentProjectId !== projectId) {
      setSelects(selectsInitialValue)
      setShowAllOptions(true)
      scrollToTopScreen()
      setCurrentProjectId(projectId)
    }
  }, [projectId, scrollToTopScreen, currentProjectId])

  useEffect(() => {
    onChange(selectedLocationNames)
  }, [selectedLocationNames, onChange])

  return (
    <div>
      <PanelContainer>
        {canImportProjects && isClientAdmin && (
          <MultiSelect
            uniqueKey="projects"
            placeholder={<FormattedMessage id="Search project" />}
            options={locationsOptions?.projects}
            value={selects?.projects}
            onChange={options => handleFilterValueChange('projects', options)}
            isDisabled={disabled}
          />
        )}
        <MultiSelect
          uniqueKey="state"
          placeholder={
            regionCodes.length === 1 ? (
              <>
                <FormattedMessage
                  id={`Search {locationType}`}
                  values={{
                    locationType: intl.formatMessage({
                      id: getLocalizedAddressVersion(
                        'administrativeArea',
                        regionCodes[0]
                      ),
                    }),
                  }}
                />
              </>
            ) : (
              <FormattedMessage id="Search state" />
            )
          }
          options={locationsOptions.state}
          value={selects?.state}
          onChange={options => handleFilterValueChange('state', options)}
          isDisabled={disabled}
        />
        <MultiSelect
          uniqueKey="city"
          placeholder={
            regionCodes.length === 1 ? (
              <>
                <FormattedMessage
                  id={`Search {locationType}`}
                  values={{
                    locationType: intl.formatMessage({
                      id: getLocalizedAddressVersion(
                        'locality',
                        regionCodes[0]
                      ),
                    }),
                  }}
                />
              </>
            ) : (
              <FormattedMessage id="Search city" />
            )
          }
          options={locationsOptions.city}
          value={selects?.city}
          onChange={options => handleFilterValueChange('city', options)}
          isDisabled={disabled}
        />
        <MultiSelect
          uniqueKey="local-area"
          placeholder={
            regionCodes.length === 1 ? (
              <>
                <FormattedMessage
                  id={`Search {locationType}`}
                  values={{
                    locationType: intl.formatMessage({
                      id: getLocalizedAddressVersion(
                        'sublocality',
                        regionCodes[0]
                      ),
                    }),
                  }}
                />
              </>
            ) : (
              <FormattedMessage id="Search local area" />
            )
          }
          options={locationsOptions.area}
          value={selects?.area}
          onChange={options => handleFilterValueChange('area', options)}
          isDisabled={disabled}
        />
        <MultiSelect
          uniqueKey="location"
          placeholder={<FormattedMessage id="Search location" />}
          options={locationsOptions.location}
          value={selects?.location}
          onChange={options => handleFilterValueChange('location', options)}
          isDisabled={disabled}
          renderOption={({ label: location }) => (
            <div className="col">
              <span>{location?.title ?? ''}</span>
              <small style={{ display: 'block', color: '#8D8D8D' }}>
                {location?.address ?? ''}
              </small>
              <small style={{ display: 'block', color: '#8D8D8D' }}>
                Store code: {location?.storeCode ?? ''}
              </small>
            </div>
          )}
        />
        <Hide condition={noLabel}>
          <MultiSelect
            uniqueKey="label"
            placeholder={<FormattedMessage id="Search label" />}
            options={locationsOptions.labels}
            value={selects?.labels}
            onChange={options => handleFilterValueChange('labels', options)}
            isDisabled={disabled}
          />
        </Hide>
      </PanelContainer>
    </div>
  )
}

LocationNamesFilterWithParams.propTypes = {
  onChange: PT.func,
  onUpdate: PT.func,
  onInit: PT.func,
  disabled: PT.bool,
  noLabel: PT.bool,
}

LocationNamesFilterWithParams.defaultProps = {
  onChange: () => {},
  onUpdate: () => {},
  onInit: () => {},
  disabled: false,
  noLabel: false,
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`

export default LocationNamesFilterWithParams
